import React, { useState, useEffect } from 'react';
import { activityApi } from '../services/api';
import { Badge } from './ui/badge';
import {
  Clock, Edit2, Plus, MessageSquare, Paperclip, ArrowRight,
  ChevronDown, ChevronUp
} from 'lucide-react';

const actionIcons = {
  created: <Plus className="w-3.5 h-3.5 text-green-600" />,
  updated: <Edit2 className="w-3.5 h-3.5 text-blue-600" />,
  comment_added: <MessageSquare className="w-3.5 h-3.5 text-purple-600" />,
  attachment_added: <Paperclip className="w-3.5 h-3.5 text-orange-600" />,
};

const actionLabels = {
  created: 'Created',
  updated: 'Edited',
  comment_added: 'Commented',
  attachment_added: 'Attached file',
};

const fieldLabels = {
  title: 'Title', description: 'Description', status: 'Status',
  priority: 'Priority', assignee_id: 'Assignee', due_date: 'Due date',
  start_date: 'Start date', estimated_hours: 'Estimated hours',
  request_type: 'Request type', tags: 'Tags', needs_help: 'Help flag',
};

const formatValue = (key, val) => {
  if (val === null || val === undefined || val === '') return 'none';
  if (key === 'status') {
    const map = { todo: 'To Do', in_progress: 'In Progress', review: 'In Review', done: 'Complete' };
    return map[val] || val;
  }
  if (key === 'needs_help') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) return val.join(', ') || 'none';
  return String(val);
};

const ActivityLog = ({ entityType, entityId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!entityId) return;
    const fetch = async () => {
      try {
        const res = await activityApi.getAll(entityType, entityId);
        setActivities(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetch();
  }, [entityType, entityId]);

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div data-testid="activity-log">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left mb-3"
        data-testid="activity-log-toggle"
      >
        <Clock className="w-4 h-4 text-navy/50" />
        <span className="text-sm font-semibold text-[#1A2B5E]">Activity</span>
        <Badge variant="outline" className="text-xs">{activities.length}</Badge>
        {expanded ? <ChevronUp className="w-4 h-4 text-navy/40 ml-auto" /> : <ChevronDown className="w-4 h-4 text-navy/40 ml-auto" />}
      </button>

      {expanded && (
        <div className="space-y-0 ml-2 border-l-2 border-navy/10 pl-4">
          {loading ? (
            <p className="text-sm text-navy/40 py-2">Loading activity...</p>
          ) : activities.length === 0 ? (
            <p className="text-sm text-navy/40 py-2">No activity yet</p>
          ) : activities.map(act => (
            <div key={act.id} className="relative pb-4 last:pb-0" data-testid={`activity-${act.id}`}>
              <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-white border-2 border-navy/20" />
              <div className="flex items-start gap-2">
                {actionIcons[act.action] || <Edit2 className="w-3.5 h-3.5 text-gray-400" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="font-medium text-[#1A2B5E]">{act.user_name}</span>
                    {' '}
                    <span className="text-navy/60">{actionLabels[act.action] || act.action}</span>
                  </p>

                  {/* Show change details */}
                  {act.action === 'updated' && act.details?.changes && (
                    <div className="mt-1 space-y-0.5">
                      {Object.entries(act.details.changes).map(([key, change]) => (
                        <div key={key} className="flex items-center gap-1 text-xs text-navy/50 flex-wrap">
                          <span className="font-medium">{fieldLabels[key] || key}:</span>
                          <span className="line-through text-red-400/70">{formatValue(key, change.old)}</span>
                          <ArrowRight className="w-3 h-3" />
                          <span className="text-green-600">{formatValue(key, change.new)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {act.action === 'comment_added' && act.details?.preview && (
                    <p className="text-xs text-navy/50 mt-0.5 italic">"{act.details.preview}"</p>
                  )}

                  {act.action === 'attachment_added' && act.details?.filename && (
                    <p className="text-xs text-navy/50 mt-0.5">{act.details.filename}</p>
                  )}

                  <p className="text-[10px] text-navy/30 mt-0.5">{timeAgo(act.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
